import React, { useState } from 'react';
import { toast } from 'sonner';
import { adminAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { UserPlus, Copy, Check } from 'lucide-react';

const emptyForm = { name: '', email: '', role: 'recruiter', send_welcome_email: true };

export const AdminCreateUserDialog = ({ open, onClose, currentUser, onCreated }) => {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const reset = () => { setForm(emptyForm); setCreated(null); setCopied(false); };

  const close = () => { reset(); onClose(); };

  const submit = async () => {
    if (!form.name.trim()) return toast.error('Enter a name');
    if (!form.email.trim()) return toast.error('Enter an email');
    setSaving(true);
    try {
      const { data } = await adminAPI.createUser({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        role: form.role,
        send_welcome_email: form.send_welcome_email,
      });
      setCreated({ user: data, emailed: form.send_welcome_email });
      toast.success(`${data.role} account created for ${data.email}`);
      onCreated && onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const copyCreds = () => {
    navigator.clipboard?.writeText(created.user.email);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const inputStyle = { width: '100%', padding: '0.625rem 0.875rem', borderRadius: 8, border: '1px solid #CBD5E1', background: '#FAF7F2', fontSize: '0.875rem', outline: 'none' };
  const labelStyle = { display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#64748B', marginBottom: '0.375rem' };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, maxWidth: 480 }} data-testid="admin-create-user-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Cabinet Grotesk', display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserPlus size={18} color="#0076A3" /> {created ? 'User Created' : 'Create User'}
          </DialogTitle>
        </DialogHeader>

        {created ? (
          <div>
            <div style={{ padding: '1rem', borderRadius: 10, background: '#F0FDF4', border: '1px solid #BBF7D0', marginBottom: '1rem' }} data-testid="create-user-success">
              <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#059669', marginBottom: 8 }}>{created.user.name} · {created.user.role}</div>
              <div style={{ fontSize: '0.8125rem', color: '#334155', fontFamily: 'monospace' }}>
                <div><strong>Email:</strong> {created.user.email}</div>
              </div>
              <Button variant="outline" onClick={copyCreds} data-testid="copy-creds-btn" style={{ marginTop: 12, borderRadius: 8, fontSize: '0.75rem' }}>
                {copied ? <><Check size={13} className="mr-1" /> Copied</> : <><Copy size={13} className="mr-1" /> Copy email</>}
              </Button>
              <p style={{ fontSize: '0.6875rem', color: '#64748B', marginTop: 10 }}>
                {created.emailed ? 'A welcome email was sent.' : 'Let them know their account is ready.'} They sign in by entering this email and the 6-digit code we send them — no password needed.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={reset} style={{ borderRadius: 10 }} data-testid="create-another-btn">Create another</Button>
              <Button onClick={close} style={{ background: '#0076A3', color: '#fff', borderRadius: 999, fontWeight: 600 }} data-testid="create-user-done-btn">Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem', marginBottom: '1.25rem' }}>
              <div>
                <label style={labelStyle}>Full name</label>
                <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Jane Doe" style={inputStyle} data-testid="create-user-name" />
              </div>
              <div>
                <label style={labelStyle}>Email</label>
                <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="jane@company.com" style={inputStyle} data-testid="create-user-email" />
              </div>
              <div>
                <label style={labelStyle}>Role</label>
                <select value={form.role} onChange={(e) => set('role', e.target.value)} style={{ ...inputStyle, color: '#0076A3' }} data-testid="create-user-role">
                  <option value="candidate">Candidate</option>
                  <option value="recruiter">Recruiter</option>
                  {currentUser?.role === 'super_admin' && <option value="admin">Admin</option>}
                </select>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8125rem', color: '#334155', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.send_welcome_email} onChange={(e) => set('send_welcome_email', e.target.checked)} data-testid="create-user-email-welcome" />
                Email the user a welcome note with sign-in instructions
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={close} style={{ borderRadius: 10 }} data-testid="create-user-cancel">Cancel</Button>
              <Button onClick={submit} disabled={saving} style={{ background: '#0076A3', color: '#fff', borderRadius: 999, fontWeight: 600 }} data-testid="create-user-submit">
                {saving ? 'Creating…' : 'Create User'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
